import React from 'react';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import ToggleSwitch from '../../../components/shared/ToggleSwitch';
import {
  TrendingUp,
  TrendingDown,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Check,
  BarChart3,
  Zap,
  Cpu,
  Server,
  HardDrive,
  Database
} from 'lucide-react';

const MonitoringPredictions = ({
  predictionConfig,
  predictions,
  savingPredictionConfig,
  generatingPredictions,
  onSavePredictionConfig,
  onGeneratePredictions,
  updatePredictionConfig,
  updatePredictionMetric,
  updatePredictionThreshold,
  getSeverityConfig
}) => {
  if (!predictionConfig) return null;

  return (
    <div className="space-y-6">
      {/* Predictions Header */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-indigo-600" />
              Predictive Analytics
            </h3>
            <p className="text-sm text-gray-600">AI-powered forecasting of system resource usage and potential issues</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={onGeneratePredictions}
              disabled={generatingPredictions}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {generatingPredictions ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <TrendingUp className="h-4 w-4 mr-2" />
              )}
              Generate Predictions
            </Button>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-4 bg-indigo-50 rounded-lg">
            <p className="text-sm text-indigo-600">Prediction Status</p>
            <p className="text-xl font-bold text-indigo-800">
              {predictionConfig.enabled ? 'Active' : 'Disabled'}
            </p>
          </div>
          <div className="p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-600">Forecast Window</p>
            <p className="text-xl font-bold text-blue-800">{predictionConfig.forecast_hours || 72}h</p>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg">
            <p className="text-sm text-purple-600">Data Window</p>
            <p className="text-xl font-bold text-purple-800">{predictionConfig.trend_window_hours || 24}h</p>
          </div>
          <div className="p-4 bg-orange-50 rounded-lg">
            <p className="text-sm text-orange-600">Active Warnings</p>
            <p className="text-xl font-bold text-orange-800">{predictions?.warning_count || 0}</p>
          </div>
        </div>

        {/* Prediction Warnings */}
        {predictions?.warnings && predictions.warnings.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium mb-3 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Trend Warnings
            </h4>
            <div className="space-y-3">
              {predictions.warnings.map((warning, idx) => {
                const severityConfig = getSeverityConfig(warning.severity);
                return (
                  <div 
                    key={idx} 
                    className={`p-4 rounded-lg border ${severityConfig.bgColor} ${severityConfig.color.replace('bg-', 'border-').split(' ')[0]}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${severityConfig.color.split(' ')[0]}`}>
                        <TrendingUp className={`h-5 w-5 ${severityConfig.color.split(' ')[1]}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={severityConfig.color}>{warning.severity}</Badge>
                          <Badge className="bg-gray-100 text-gray-700">{warning.metric}</Badge>
                        </div>
                        <p className="font-medium">{warning.message}</p>
                        <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
                          <span>Current: <strong>{warning.current_value}%</strong></span>
                          {warning.hours_until > 0 && (
                            <span>ETA: <strong>{warning.hours_until}h</strong></span>
                          )}
                          <span>Confidence: <strong>{warning.confidence}%</strong></span>
                        </div>
                        {warning.predicted_time && (
                          <p className="text-xs text-gray-400 mt-1">
                            Predicted at: {new Date(warning.predicted_time).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {(!predictions?.warnings || predictions.warnings.length === 0) && predictions?.status === 'success' && (
          <div className="p-4 bg-green-50 rounded-lg mb-6">
            <div className="flex items-center gap-2 text-green-800">
              <CheckCircle className="h-5 w-5" />
              <p className="font-medium">All Clear!</p>
            </div>
            <p className="text-sm text-green-700 mt-1">
              No concerning trends detected. All metrics are within expected ranges.
            </p>
          </div>
        )}

        {predictions?.status === 'no_data' && (
          <div className="p-4 bg-yellow-50 rounded-lg mb-6">
            <div className="flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="h-5 w-5" />
              <p className="font-medium">Insufficient Data</p>
            </div>
            <p className="text-sm text-yellow-700 mt-1">
              Not enough historical data available for predictions. The system will automatically collect metrics over time.
            </p>
          </div>
        )}
      </Card>

      {/* Metric Predictions */}
      {predictions?.predictions && Object.keys(predictions.predictions).length > 0 && (
        <Card className="p-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-gray-600" />
            Metric Forecasts
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(predictions.predictions).map(([metric, data]) => {
              if (data.status !== 'analyzed') {
                return (
                  <div key={metric} className="p-4 bg-gray-50 rounded-lg">
                    <p className="font-medium capitalize">{metric.replace('_', ' ')}</p>
                    <p className="text-sm text-gray-500">{data.status}: {data.data_points || 0}/{data.required_points || 0} data points</p>
                  </div>
                );
              }
              
              const trendIcon = data.trend === 'increasing' ? '📈' : data.trend === 'decreasing' ? '📉' : '➡️';
              const trendColor = data.trend === 'increasing' ? 'text-red-600' : data.trend === 'decreasing' ? 'text-green-600' : 'text-gray-600';
              
              return (
                <div key={metric} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <p className="font-medium capitalize flex items-center gap-2">
                      {metric === 'cpu' && <Cpu className="h-4 w-4" />}
                      {metric === 'memory' && <Server className="h-4 w-4" />}
                      {metric === 'disk' && <HardDrive className="h-4 w-4" />}
                      {metric === 'db_connections' && <Database className="h-4 w-4" />}
                      {metric.replace('_', ' ')}
                    </p>
                    <span className={`text-sm font-medium ${trendColor}`}>
                      {trendIcon} {data.trend}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm mb-3">
                    <div>
                      <p className="text-gray-500">Current</p>
                      <p className="font-semibold">{data.current_value}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Threshold</p>
                      <p className="font-semibold">{data.threshold}%</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Daily Change</p>
                      <p className={`font-semibold ${data.daily_change > 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {data.daily_change > 0 ? '+' : ''}{data.daily_change}%
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-500">Confidence</p>
                      <p className="font-semibold">{data.confidence}%</p>
                    </div>
                  </div>
                  
                  {data.predictions && data.predictions.length > 0 && (
                    <div className="border-t pt-2">
                      <p className="text-xs text-gray-500 mb-1">Forecasted Values:</p>
                      <div className="flex flex-wrap gap-2">
                        {data.predictions.slice(0, 4).map((pred, idx) => (
                          <span key={idx} className="text-xs bg-white px-2 py-1 rounded border">
                            +{pred.hours_ahead}h: <strong>{pred.predicted_value}%</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {data.threshold_crossing && (
                    <div className="mt-2 p-2 bg-orange-100 rounded text-sm text-orange-800">
                      ⚠️ May reach {data.threshold}% in ~{data.threshold_crossing.hours_until}h
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Prediction Configuration */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-semibold">Prediction Settings</h3>
            <p className="text-sm text-gray-600">Configure predictive analytics parameters</p>
          </div>
          <Button
            onClick={onSavePredictionConfig}
            disabled={savingPredictionConfig}
            className="bg-indigo-600 hover:bg-indigo-700"
          >
            {savingPredictionConfig ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Check className="h-4 w-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>

        <div className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="font-medium">Enable Predictive Analytics</p>
              <p className="text-sm text-gray-600">Forecast future resource usage based on trends</p>
            </div>
            <ToggleSwitch
              enabled={predictionConfig.enabled}
              onToggle={() => updatePredictionConfig('enabled', !predictionConfig.enabled)}
              activeColor="bg-indigo-600"
            />
          </div>

          {/* Time Windows */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700">Forecast Window (hours)</label>
              <Input
                type="number"
                value={predictionConfig.forecast_hours || 72}
                onChange={(e) => updatePredictionConfig('forecast_hours', parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">How far ahead to predict</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Data Window (hours)</label>
              <Input
                type="number"
                value={predictionConfig.trend_window_hours || 24}
                onChange={(e) => updatePredictionConfig('trend_window_hours', parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Historical data for trend calculation</p>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700">Min Data Points</label>
              <Input
                type="number"
                value={predictionConfig.min_data_points || 12}
                onChange={(e) => updatePredictionConfig('min_data_points', parseInt(e.target.value))}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">Required for prediction</p>
            </div>
          </div>

          {/* Metrics to Predict */}
          <div>
            <label className="font-medium">Metrics to Predict</label>
            <p className="text-sm text-gray-600 mb-3">Select which metrics to forecast</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { key: 'cpu', label: 'CPU Usage', icon: Cpu },
                { key: 'memory', label: 'Memory', icon: Server },
                { key: 'disk', label: 'Disk Usage', icon: HardDrive },
                { key: 'db_connections', label: 'DB Connections', icon: Database }
              ].map(({ key, label, icon: Icon }) => (
                <div
                  key={key}
                  onClick={() => updatePredictionMetric(key, !predictionConfig.metrics_to_predict?.[key])}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors flex items-center gap-3 ${
                    predictionConfig.metrics_to_predict?.[key]
                      ? 'bg-indigo-50 border-indigo-300'
                      : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <Icon className={`h-5 w-5 ${predictionConfig.metrics_to_predict?.[key] ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <span className={predictionConfig.metrics_to_predict?.[key] ? 'text-indigo-800 font-medium' : 'text-gray-600'}>
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Alert Thresholds */}
          <div>
            <label className="font-medium">Prediction Alert Thresholds</label>
            <p className="text-sm text-gray-600 mb-3">Alert when predicted to reach these levels</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { key: 'cpu', label: 'CPU (%)' },
                { key: 'memory', label: 'Memory (%)' },
                { key: 'disk', label: 'Disk (%)' },
                { key: 'db_connections', label: 'DB Connections' }
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="text-sm text-gray-600">{label}</label>
                  <Input
                    type="number"
                    value={predictionConfig.alert_thresholds?.[key] || 90}
                    onChange={(e) => updatePredictionThreshold(key, e.target.value)}
                    className="mt-1"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </Card>

      {/* How it Works */}
      <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50">
        <h4 className="font-semibold mb-3 flex items-center gap-2">
          <Zap className="h-5 w-5 text-indigo-600" />
          How Predictive Analytics Works
        </h4>
        <div className="text-sm text-gray-700 space-y-2">
          <p>
            <strong>Linear Regression:</strong> The system analyzes historical metric data to identify trends 
            and calculate the rate of change over time.
          </p>
          <p>
            <strong>Forecast Calculation:</strong> Using the trend line, we project future values at 6h, 12h, 24h, 
            48h, and 72h intervals.
          </p>
          <p>
            <strong>Threshold Warnings:</strong> When a metric is trending toward a critical threshold, you'll receive 
            advance warning with an estimated time of arrival.
          </p>
          <p>
            <strong>Confidence Score:</strong> Based on R-squared value and data quality, indicating how reliable 
            the prediction is (higher = more confident).
          </p>
        </div>
      </Card>
    </div>
  );
};

export default MonitoringPredictions;
